/**
 * @fileoverview This module defines the actions that can be performed by the Service Worker,
 * along with related types and utility functions.
 */

import {ServiceWorkerModel} from '../service-worker-model/service-worker.model';

/**
 * @function ServiceWorkerActions
 * @description Factory function that creates an object with methods representing Service Worker actions
 * @param {ServiceWorkerModel} swModel - The Service Worker model
 */
export const ServiceWorkerActions = (swModel: ServiceWorkerModel) => ({
	ping: async () => 'pong',
	clientReloaded: async () => await swModel.handleClientReloaded(),
	getVersion: async () => await swModel.Version.loadFromCache(),
	checkForUpdates: async () => await swModel.Version.loadFromServer(),
	skipWaiting: async () => await swModel.Version.skipWaiting(),
	getConfig: async () => await swModel.SwConfig.loadFromCache(),
	getLogs: async () => await swModel.Logs.getLogs(),
	clearLogs: async () => await swModel.Logs.clearLogs(),
	getCacheAnalysis: async () => await swModel.Cache.getCacheAnalysis(),
	clearSourceCaches: async (reload = false) => await swModel.Cache.clearSourceCaches(reload),
	clearCacheDeltas: async (cacheUrlsDelta?: string[]) => await swModel.Cache.deleteCacheDeltas(cacheUrlsDelta),
});

// Type definitions
type ServiceWorkerActionsType = ReturnType<typeof ServiceWorkerActions>;
export type ServiceWorkerActionType = keyof ServiceWorkerActionsType;
export type ServiceWorkerActionParams<T extends ServiceWorkerActionType> = Parameters<ServiceWorkerActionsType[T]>;
export type ServiceWorkerActionResult<T extends ServiceWorkerActionType> = Awaited<ReturnType<ServiceWorkerActionsType[T]>>;

/**
 * @interface INewServiceWorkerMessage
 * @description Represents a message sent to the Service Worker
 * @template T - The type of the action (must be a valid ServiceWorkerActionType)
 */
export interface INewServiceWorkerMessage<T extends ServiceWorkerActionType = ServiceWorkerActionType> {
	action: T;
	params: ServiceWorkerActionParams<T>;
}

/**
 * @interface IExtendableMessageEvent
 * @description Extends the ExtendableMessageEvent with typed data
 */
export type IExtendableMessageEvent = ExtendableMessageEvent & {
	data: INewServiceWorkerMessage;
};

/**
 * @function isServiceWorkerActionType
 * @description Type guard to check if a value is a valid ServiceWorkerActionType
 * @param {unknown} value - The value to check
 * @returns {boolean} True if the value is a valid ServiceWorkerActionType
 */
export function isServiceWorkerActionType(value: unknown): value is ServiceWorkerActionType {
	return typeof value === 'string' && value in ServiceWorkerActions({} as any);
}

/**
 * @function isServiceWorkerMessageEvent
 * @description Type guard to check if an event is a valid Service Worker message event
 * @param {any} e - The event to check
 * @returns {boolean} True if the event is a valid Service Worker message event
 */
export function isServiceWorkerMessageEvent(e: any): e is INewServiceWorkerMessage {
	return e?.data?.action && isServiceWorkerActionType(e.data.action);
}

/**
 * @function isServiceWorkerMessage
 * @description Type guard to check if an object is a valid Service Worker message
 * @param {any} message - The object to check
 * @returns {boolean} True if the object is a valid Service Worker message
 */
export function isServiceWorkerMessage(message: any): message is INewServiceWorkerMessage {
	return message?.action && isServiceWorkerActionType(message.action);
}

/**
 * @function isActionReturnResponse
 * @description Checks if an action returns a response
 * @param {ServiceWorkerActionType} actionName - The name of the action to check
 * @returns {boolean} True if the action returns a response
 */
export function isActionReturnResponse<T extends ServiceWorkerActionType>(actionName: T): boolean {
	const func = ServiceWorkerActions({} as any)[actionName];
	return isFnReturnValue(func);
}

/**
 * @function isFnReturnValue
 * @description Checks if a function returns a value
 * @param {Function} fn - The function to check
 * @returns {boolean} True if the function returns a value
 */
const isFnReturnValue = (fn: Function): boolean => {
	if (typeof fn !== 'function') return false;
	const fnStr = fn.toString().replace(/\s/g, '');
	// Check for regular functions with 'return' keyword
	if (fnStr.includes('return')) return true;
	// Check for arrow functions with implicit return
	return /=>(\{.*return|[^{])/.test(fnStr);
};
